import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp, TableCompData } from "../../../../components";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import InvestorPortalPageHeader, {
  InvestorPortalPrimaryButton,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalPageHeader";
import InvestorPortalTableEmpty from "../../../../components/Admin/InvestorPortal/InvestorPortalTableEmpty";
import InvestorPortalTableSection, {
  TableRowLink,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalTableSection";
import TableActionDropdown from "../../../../components/Admin/InvestorPortal/TableActionDropdown";
import {
  ProductStatusBadge,
  StockStatusBadge,
} from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { PRODUCT_STATUS } from "../../../../store/admin/marketplace/constants";
import { formatMoney } from "../../../../utils/currencyFormat";
import { staffMarketplaceProductPath } from "../../../../utils/staffMarketplaceAdapter";
import { useListStaffMarketplaceProductsQuery } from "../../../../store/admin/marketplace/staffMarketplaceApi";
import moment from "moment";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

const PER_PAGE = 10;

function ProductsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const apiQuery = useListStaffMarketplaceProductsQuery({
    page,
    per_page: PER_PAGE,
    search: search || undefined,
    status: statusFilter || undefined,
  });

  const items = useMemo(() => apiQuery.data?.items || [], [apiQuery.data]);
  const apiPagination = apiQuery.data?.pagination || {};
  const pagination = {
    currentPage: apiPagination.current_page || page,
    totalPages: apiPagination.last_page || 1,
    total: apiPagination.total || items.length,
    perPage: apiPagination.per_page || PER_PAGE,
    onPageChange: setPage,
  };

  const hasFilters = Boolean(search || statusFilter);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const emptyState = apiQuery.isError ? (
    <InvestorPortalTableEmpty
      variant="error"
      title="We couldn’t load marketplace products."
      onCta={() => apiQuery.refetch()}
    />
  ) : hasFilters ? (
    <InvestorPortalTableEmpty
      variant="filtered"
      title="No products match your filters."
      onCta={clearFilters}
    />
  ) : (
    <InvestorPortalTableEmpty
      variant="empty"
      title="No marketplace products yet."
      description="Create your first product to publish to the investor marketplace."
      ctaLabel="New product"
      onCta={() => navigate("/super_admin/investor_portal/marketplace/products/new")}
    />
  );

  const columns = [
    {
      name: "Product",
      selector: (row) => row.name,
      cell: (row) => (
        <TableRowLink
          onClick={() => navigate(staffMarketplaceProductPath(row))}
        >
          {row.name}
        </TableRowLink>
      ),
      minWidth: "200px",
      grow: 2,
    },
    { name: "SKU", selector: (row) => row.sku || "—", minWidth: "100px" },
    {
      name: "Category",
      cell: (row) => (
        <span>
          {row.categoryName || "—"}
          {row.subcategoryName ? (
            <span className="text-muted small d-block">{row.subcategoryName}</span>
          ) : null}
        </span>
      ),
      minWidth: "130px",
    },
    {
      name: "Status",
      cell: (row) => <ProductStatusBadge status={row.lifecycleStatus || row.status} />,
      minWidth: "130px",
    },
    {
      name: "Stock",
      cell: (row) => <StockStatusBadge status={row.stockStatus} />,
      minWidth: "120px",
    },
    {
      name: "Qty",
      selector: (row) => (row.stockQuantity != null ? row.stockQuantity : "—"),
      minWidth: "70px",
      center: true,
    },
    {
      name: "Price",
      selector: (row) => formatMoney(row.priceNgn ?? row.price),
      minWidth: "120px",
      right: true,
    },
    {
      name: "Updated",
      selector: (row) => (row.updatedAt ? moment(row.updatedAt).format("DD-MMM-YY") : "—"),
      minWidth: "100px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <TableActionDropdown
          items={[
            {
              name: "View",
              action: () => navigate(staffMarketplaceProductPath(row)),
            },
            {
              name: "Edit",
              action: () => navigate(staffMarketplaceProductPath(row, "/edit")),
            },
          ]}
          row={row}
        />
      ),
      width: "80px",
      center: true,
    },
  ];

  return (
    <div className="pb-4">
      <InvestorPortalPageHeader
        title="Marketplace Products"
        action={
          <InvestorPortalPrimaryButton
            btnText="New product"
            onClick={() => navigate("/super_admin/investor_portal/marketplace/products/new")}
          />
        }
      />

      <CardComp cardClassName="border-0 shadow-sm mb-3 investor-portal-filter-bar">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-5">
            <label className="form-label small fw-semibold">Search</label>
            <input
              className="form-control"
              placeholder="Search by name or SKU"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              {Object.values(PRODUCT_STATUS).map((status) => (
                <option key={status} value={status}>
                  {status.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <button type="button" className="btn btn-outline-secondary w-100" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        </div>
      </CardComp>

      <InvestorPortalTableSection pagination={pagination}>
        <TableCompData
          columns={columns}
          data={items}
          progressPending={apiQuery.isFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={9} />}
          noDataComponent={emptyState}
        />
      </InvestorPortalTableSection>
    </div>
  );
}

export default withMarketplaceGuard(ProductsListPage);
